import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Check, X, MapPin, User, ShieldCheck, Clock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import AdminListingActions from "./admin/AdminListingActions";
import { resolveMediaUrl } from "../lib/fileUrl";
import { toast } from "sonner";
import { formatLocalTimestamp } from "../lib/dateLocal";

const SPACE_TYPE_LABELS = {
  driveway: "Driveway",
  garage: "Garage",
  street: "Street",
  underground: "Underground",
  carport: "Carport",
  ev_bay: "EV bay",
  reserved_spot: "Reserved spot",
  commercial_lot: "Commercial lot",
};

const FACILITY_LABELS = {
  camera: "Camera",
  security_24_7: "24/7 security",
  ev_charging: "EV charging",
  covered_roof: "Covered roof",
  gated: "Gated",
  fenced: "Fenced",
};

export default function AdminParkingReview({ spotId, open, onClose, onApprove, onReject, onDelete, onPause, onResume }) {
  const [spot, setSpot] = useState(null);
  const [host, setHost] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (!open || !spotId) {
      setSpot(null);
      setHost(null);
      setNotes([]);
      setRejectOpen(false);
      setRejectReason("");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/parking/${spotId}/review`,
          { withCredentials: true },
        );
        if (cancelled) return;
        setSpot(data.spot);
        setHost(data.host);
        setNotes(data.notes || []);
      } catch (e) {
        if (!cancelled) {
          toast.error(e.response?.data?.detail || "Could not load parking details");
          onClose?.();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, spotId, onClose]);

  const handleApprove = async () => {
    setActing(true);
    try {
      await onApprove?.(spotId);
      onClose?.();
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    setActing(true);
    try {
      await onReject?.(spotId, rejectReason.trim());
      setRejectOpen(false);
      onClose?.();
    } finally {
      setActing(false);
    }
  };

  const photos = (spot?.images || []).map((url) => resolveMediaUrl(url)).filter(Boolean);
  const uniquePhotos = [...new Set(photos)];

  const kycColor = (s) => {
    if (s === "approved") return "var(--zc-success)";
    if (s === "rejected" || s === "declined") return "var(--zc-danger,#dc2626)";
    if (s === "manual_review") return "var(--zc-warn,#f59e0b)";
    return "var(--zc-text-3)";
  };

  const hoursLabel = spot?.open_24_7
    ? "Open 24/7"
    : spot?.open_time && spot?.close_time
      ? `${spot.open_time} – ${spot.close_time} daily`
      : "—";

  const amenityLabels = [
    SPACE_TYPE_LABELS[spot?.space_type] || spot?.space_type,
    ...(spot?.facilities || []).map((f) => FACILITY_LABELS[f] || f),
  ].filter(Boolean);

  const amendedFields = spot?.pending_changes?.fields?.length
    ? spot.pending_changes.fields
    : (notes.find((n) => n.changes?.length)?.changes || []);

  return (
    <>
      <Dialog open={open && !rejectOpen} onOpenChange={(o) => !o && onClose?.()}>
        <DialogContent
          className="bg-white border border-[var(--zc-border)] max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="admin-parking-review"
        >
          <DialogHeader>
            <DialogTitle className="zc-headline text-2xl">
              {loading ? "Loading…" : spot ? (spot.name || "Parking listing") : "Parking review"}
            </DialogTitle>
          </DialogHeader>

          {loading && (
            <div className="py-12 text-center text-sm text-[var(--zc-text-3)]">Fetching listing profile…</div>
          )}

          {!loading && spot && (
            <div className="space-y-6 mt-2">
              {amendedFields.length > 0 && (
                <div className="zc-card p-4 border border-[var(--zc-warn,#f59e0b)]/40" data-testid="admin-parking-edit-diff">
                  <div className="eyebrow mb-2">Host edit — amended fields</div>
                  <ul className="space-y-2 text-sm">
                    {amendedFields.map((c) => (
                      <li key={c.field} className="flex flex-wrap gap-x-2 text-[var(--zc-text-2)]">
                        <span className="font-semibold text-[var(--zc-text)]">{c.label}:</span>
                        <span className="line-through opacity-70">{c.before}</span>
                        <span>→</span>
                        <span className="text-[var(--zc-accent)] font-semibold">{c.after}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {notes.length > 0 && (
                <div className="zc-card p-4" data-testid="admin-parking-notes">
                  <div className="eyebrow mb-2">Notes</div>
                  <ul className="space-y-2 text-sm text-[var(--zc-text-2)]">
                    {notes.map((n) => (
                      <li key={n.note_id}>
                        <div>{n.body}</div>
                        <div className="text-caption text-[var(--zc-text-3)] mt-0.5">{formatLocalTimestamp(n.created_at)}</div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {uniquePhotos.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Photos ({uniquePhotos.length})</div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {uniquePhotos.map((src, i) => (
                      <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="block aspect-[4/3] rounded-lg overflow-hidden bg-[var(--zc-bg-3)]">
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="zc-card p-4">
                  <div className="eyebrow mb-2">Pricing</div>
                  <div className="zc-price text-2xl">${spot.price_per_hour?.toFixed?.(2) ?? spot.price_per_hour}<span className="zc-unit normal-case tracking-normal"> /hr</span></div>
                  {spot.max_hours != null && (
                    <div className="text-xs text-[var(--zc-text-3)] mt-1">Max stay {spot.max_hours} hours per booking</div>
                  )}
                </div>
                <div className="zc-card p-4">
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><Clock size={12} /> Hours</div>
                  <div className="text-[var(--zc-text-2)]">{hoursLabel}</div>
                </div>
              </div>

              {amenityLabels.length > 0 && (
                <div>
                  <div className="eyebrow mb-2">Amenities</div>
                  <div className="flex flex-wrap gap-2">
                    {amenityLabels.map((label) => (
                      <span key={label} className="px-2 py-1 text-xs rounded-full border border-[var(--zc-border)] text-[var(--zc-text-2)]">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="zc-card p-4">
                <div className="eyebrow mb-2 flex items-center gap-1.5"><User size={12} /> Host</div>
                <div className="text-sm font-semibold">{spot.host_name || host?.name || "—"}</div>
                {host?.email && <div className="text-xs text-[var(--zc-text-3)] mt-0.5">{host.email}</div>}
                <div className="mt-2 flex items-center gap-1.5 text-xs">
                  <ShieldCheck size={12} style={{ color: kycColor(host?.kyc_status) }} />
                  <span style={{ color: kycColor(host?.kyc_status) }} className="capitalize font-semibold">
                    KYC {(host?.kyc_status || "unknown").replace(/_/g, " ")}
                  </span>
                </div>
              </div>

              <div className="zc-card p-4">
                <div className="eyebrow mb-2 flex items-center gap-1.5"><MapPin size={12} /> Location</div>
                <div className="text-sm">{spot.address || "—"}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">{spot.city}</div>
              </div>

              {spot.description && (
                <div>
                  <div className="eyebrow mb-2">Description</div>
                  <p className="text-sm text-[var(--zc-text-2)] leading-relaxed">{spot.description}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--zc-border)]">
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={acting}
                  className="px-4 h-10 zc-btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`parking-review-approve-${spotId}`}
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  type="button"
                  onClick={() => setRejectOpen(true)}
                  disabled={acting}
                  className="px-4 h-10 zc-btn-ghost text-sm flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`parking-review-reject-${spotId}`}
                >
                  <X size={14} /> Reject
                </button>
                <AdminListingActions
                  item={{ ...spot, type: "parking", listing_id: spot.spot_id }}
                  onPause={onPause}
                  onResume={onResume}
                  onDelete={onDelete ? () => onDelete(spot) : undefined}
                  size="lg"
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rejectOpen} onOpenChange={(o) => !o && setRejectOpen(false)}>
        <DialogContent className="bg-white border border-[var(--zc-border)] max-w-md" data-testid="admin-parking-reject-reason">
          <DialogHeader>
            <DialogTitle className="zc-headline text-xl">Reject parking listing</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[var(--zc-text-2)]">
            Tell the host why this listing was rejected — a reason is required.
          </p>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="e.g. Photos don't show the parking spot clearly…"
            className="zc-input w-full min-h-[88px] p-3 text-sm mt-2"
            data-testid="parking-reject-reason-input"
          />
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" onClick={() => setRejectOpen(false)} className="px-4 h-10 zc-btn-ghost text-sm">Cancel</button>
            <button
              type="button"
              onClick={handleReject}
              disabled={acting || !rejectReason.trim()}
              className="px-4 h-10 text-sm rounded-md text-white disabled:opacity-50"
              style={{ background: "var(--zc-danger)" }}
              data-testid="parking-reject-reason-submit"
            >
              {acting ? "Rejecting…" : "Reject listing"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
